/**
 * "Code activity" gadget (Build): per service, the commits and the files changed in the last 7 days (vfs-service
 * `GET /collab/changes?since=`), the branch it is on and how many files are not committed yet (`GET /git/overview`).
 * Either answer may fail on its own: what the other says is still shown, and the missing part is said plainly.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { codeActivity } from '../../../utils/homeGadgets/build';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useChangesWeek, useGitOverview } from './sources';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function CodeActivity({ projectId, onOpenGit = null, onOpenChanges = null }) {
  const changes = useChangesWeek(projectId);
  const git = useGitOverview(projectId);
  const loading = changes.loading || git.loading;
  const both = changes.error && git.error;
  const rows = codeActivity(changes.data?.changes || [], git.data?.repos || []);
  const links = (onOpenChanges || onOpenGit) ? <><TabLink onClick={onOpenChanges}>Changes</TabLink><TabLink onClick={onOpenGit}>Git</TabLink></> : null;
  return (
    <GadgetCard links={links} meta="last 7 days" testId="code-activity" title="Code activity">
      <GadgetBody
        data={changes.data || git.data}
        empty={rows.length === 0}
        emptyText="No service has code yet."
        error={both}
        errorText="The code activity could not be read just now."
        loading={loading}
        loadingText="Reading the code activity…"
      >
        {() => (
          <>
            <ul className="gd-rows">
              {rows.map((row) => (
                <li className="gd-row" data-testid={`code-${row.service || 'project'}`} key={row.service || '(project)'}>
                  <strong>{row.service || 'Project files'}</strong>
                  <span className="gd-grow ph-muted">
                    {changes.data ? `${plural(row.commits, 'commit')} · ${plural(row.files, 'file')}` : ''}
                    {row.branch ? ` · ${row.branch}` : ''}
                  </span>
                  {row.uncommitted > 0 && <span className="gd-warn gd-num">{row.uncommitted} not committed</span>}
                </li>
              ))}
            </ul>
            {changes.error && <p className="gd-note">The commits of the last 7 days could not be read just now.</p>}
            {git.error && <p className="gd-note">The branches and the changes not committed could not be read just now.</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

CodeActivity.propTypes = { onOpenChanges: PropTypes.func, onOpenGit: PropTypes.func, projectId: PropTypes.string.isRequired };
